import type { AudioFeedback } from '../services/audio';
import type { AuthService } from '../services/auth';
import type { CatalogService } from '../services/catalog';
import { CycleRecordError, type RobotGateway } from '../services/robot';
import type { TabletSetup } from '../services/tablet-setup';
import type { SyncIndicator, UpdateIndicator } from './context';
import type { EffectRunner } from './store';

export interface Services {
  readonly audio: AudioFeedback;
  readonly auth: AuthService;
  readonly catalog: CatalogService;
  readonly robot: RobotGateway;
  /** Offline mode: the tablet's sync with the server. */
  readonly sync?: SyncIndicator;
  /** Offline mode: setting the tablet up for a robot, and resetting it. */
  readonly setup?: TabletSetup;
  /** Android app: a newer version on the install page. */
  readonly updates?: UpdateIndicator;
}

/** Executes machine effects against the real services and feeds results back as events. */
export function createEffectRunner(
  { audio, auth, catalog, robot }: Services,
  reportError: (message: string, error: unknown) => void = console.error,
  now: () => number = Date.now,
): EffectRunner {
  return (effect, dispatch) => {
    switch (effect.type) {
      case 'sound':
        audio.play(effect.cue);
        return;
      case 'verifyPin':
        auth.verifyPin(effect.pin).then(
          (result) => dispatch({ type: 'PIN_VERIFIED', result }),
          (error: unknown) => {
            reportError('PIN verification failed', error);
            dispatch({ type: 'PIN_VERIFIED', result: { ok: false, reason: 'unavailable' } });
          },
        );
        return;
      case 'loadCatalog':
        // On failure the built-in ward list stays in place; the workflow is not blocked.
        catalog.load().then(
          (snapshot) => dispatch({ type: 'CATALOG_LOADED', catalog: snapshot, now: now() }),
          (error: unknown) => reportError('Failed to load ward catalog', error),
        );
        return;
      case 'robot.resumeCycle':
        robot.resumeCycle(effect.cycleId);
        return;
      case 'signOut':
        auth.signOut().catch((error: unknown) => reportError('Sign-out failed', error));
        return;
      case 'robot.startEmission':
        // If the lamp cannot be switched on, the cycle must not report the room as sanitised.
        robot.startEmission(effect.job).catch((error: unknown) => {
          reportError('Failed to start emission', error);
          dispatch({ type: 'DEVICE_FAULT' });
        });
        return;
      case 'robot.completeCycle':
        robot.completeCycle(effect.record).then(
          (record) => {
            // No backend (demo build): the local record is all there is.
            dispatch({ type: 'CYCLE_RECORDED', record: record ?? effect.record });
          },
          (error: unknown) => {
            reportError('Failed to record completed cycle', error);
            const problem = error instanceof CycleRecordError ? error.problem : 'unreachable';
            dispatch({ type: 'CYCLE_RECORD_FAILED', problem });
          },
        );
        return;
      case 'robot.emergencyStop':
        // Any failure — explicit or network — means the lamp may still be lit.
        robot.emergencyStop(effect.reason, effect.job).catch((error: unknown) => {
          reportError('EMERGENCY STOP NOT ACKNOWLEDGED', error);
          dispatch({ type: 'STOP_UNCONFIRMED' });
        });
        return;
    }
  };
}
